import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { z } from 'zod'

import { dayjsFactory } from '@bemlo/date'
import {
  HowMuchWorkAlternative,
  Locale,
  Municipality,
  WhichDepartmentToWorkInAlternative,
  WhichRegionAlternative,
  WorkForm,
} from '@bemlo/enums'
import {
  getTranslation,
  translateCountryFactory,
  translateCurrentEmploymentStatusFactory,
  translateDriversLicenseFactory,
  translateDynamicShiftStatusFactory,
  translateEmploymentTypeFactory,
  translateFileUploadTypeFactory,
  translateHowMuchWorkFactory,
  translateJobTypeFactory,
  translateJournalSystemFactory,
  translateMunicipalityFactory,
  translateNHoursAndMinutesFactory,
  translateOnCallTypeFactory,
  translateProfessionFactory,
  translateProfessionShortFactory,
  translateShiftActivityFactory,
  translateShiftHoursFactory,
  translateShiftTypeFactory,
  translateSpecializationEducationFactory,
  translateSpecializationTitleFactory,
  translateTenderPricingFactory,
  translateTenderStatusFactory,
  translateWhichDepartmentToWorkInAlternativeFactory,
  translateWhichRegionAlternativeFactory,
  translateWorkFormFactory,
  translateWorkHoursFactory,
  translateWorkPlaceRelationshipToApplicantFactory,
} from '@bemlo/i18n'
import { translateSchedulingRuleViolationFactory } from '@bemlo/schedule'

import * as en from './locales/en'
import * as nb from './locales/nb'
import * as sv from './locales/sv'
import { formatCurrency, formatCurrencyRange } from './mappers/currency'
import { translateZodErrorMap } from './mappers/zod-error-map'

interface I18nContextValue {
  locale: Locale
  setLocale: (newLocale: Locale) => void
}

const DEFAULT_LOCALE = Locale.SV

const locales: Record<Locale, typeof en> = {
  da: en, // TODO(rb): add Danish translations
  fi: en, // TODO(rb): add Finnish translations
  en,
  nb,
  sv,
}

export const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => undefined,
})

interface I18nProviderProps {
  getDefaultLocale: () => Locale
  children: ReactNode
}

export const I18nProvider = ({
  getDefaultLocale,
  children,
}: I18nProviderProps) => {
  const [locale, setLocale] = useState(getDefaultLocale)

  const value = useMemo(() => {
    return {
      locale,
      setLocale: (newLocale: Locale) => {
        window.localStorage.setItem('locale', newLocale)
        setLocale(newLocale)
      },
    }
  }, [locale])

  useEffect(() => {
    const formTranslations = getTranslation('form', locale)

    z.setErrorMap(translateZodErrorMap(formTranslations))
  }, [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useLocale = () => {
  const { locale } = useContext(I18nContext)

  return locale
}

const createLocaleTranslation = (locale: Locale) => {
  return {
    ...locales[locale],
    $locale: locale,
    $date: dayjsFactory(locale),
    $n_hours: translateNHoursAndMinutesFactory(locale),
    $country: translateCountryFactory(locale),
    $currentEmploymentStatus: translateCurrentEmploymentStatusFactory(locale),
    $driversLicense: translateDriversLicenseFactory(locale),
    $employmentType: translateEmploymentTypeFactory(locale),
    $fileUploadType: translateFileUploadTypeFactory(locale),
    $howMuchWorkAlternative:
      translateHowMuchWorkFactory<HowMuchWorkAlternative>(locale),
    $jobType: translateJobTypeFactory(locale),
    $journalSystem: translateJournalSystemFactory(locale),
    $municipality: translateMunicipalityFactory<Municipality>(locale),
    $profession: translateProfessionFactory(locale),
    $professionShort: translateProfessionShortFactory(locale),
    $shiftActivity: translateShiftActivityFactory(locale),
    $shiftHours: translateShiftHoursFactory(locale),
    $shiftType: translateShiftTypeFactory(locale),
    $specialization: translateSpecializationTitleFactory(locale),
    $specializationEducation: translateSpecializationEducationFactory(locale),
    $tenderPricing: translateTenderPricingFactory(locale),
    $tenderScheduleType: translateTenderStatusFactory(locale),
    $tenderStatus: translateTenderStatusFactory(locale),
    $howMuchWork: translateHowMuchWorkFactory(locale),
    $whichDepartmentToWorkInAlternative:
      translateWhichDepartmentToWorkInAlternativeFactory<WhichDepartmentToWorkInAlternative>(
        locale,
      ),
    $whichRegionAlternative:
      translateWhichRegionAlternativeFactory<WhichRegionAlternative>(locale),
    $workForm: translateWorkFormFactory<WorkForm>(locale),
    $workHours: translateWorkHoursFactory(locale),
    $workPlaceRelationshipToApplicant:
      translateWorkPlaceRelationshipToApplicantFactory(locale),
    $dynamicShiftStatus: translateDynamicShiftStatusFactory(locale),
    $schedulingRuleViolation: translateSchedulingRuleViolationFactory(locale),
    $currency: formatCurrency(locale),
    $currencyRange: formatCurrencyRange(locale),
    $onCallTypeAbbreviation: translateOnCallTypeFactory(locale, 'abbreviation'),
    form: getTranslation('form', locale),
    ui: getTranslation('ui', locale),
  }
}

const localeTranslations: Partial<
  Record<Locale, ReturnType<typeof createLocaleTranslation>>
> = {}

export const useTranslations = () => {
  const locale = useLocale()

  return (localeTranslations[locale] ??= createLocaleTranslation(locale))
}
